import React, { useEffect, useState } from 'react';
import { be1Fetch } from '../../../../config/api';
import type { Column } from '../../utils/transformLoad';
import { useTranslation } from 'react-i18next';
import { errorText } from '../../../../services/errorText';

interface Props {
  title: string;
  subtitle?: string;
  columns: Column[];
  loading?: boolean;
  error?: string;
  onClose: () => void;
  /** 값 미리보기 요청(있으면 '값 미리보기' 탭 표시) — POST /api/data/preview 본문 */
  preview?: { kind: 'db' | 'csv'; connection?: string; schema?: string; table?: string; filepath?: string; delimiter?: string; hasHeader?: boolean; fieldNames?: string; encoding?: string } | null;
}

/**
 * 상세보기 — SAM파일/DB테이블/내부데이터의 항목 목록(항목 보기)과 실제 값(값 보기).
 *
 * 데이터처리의 소스·타깃 상세와 DB테이블 노드의 컬럼 상세가 같은 팝업을 쓴다.
 * **크기는 고정한다** — 예전에는 값 탭을 누르면 폭이 560→960px 로 바뀌어 창이 갑자기 커졌다(현장 요청).
 * 값은 '값 보기' 탭을 고를 때 처음 한 번 읽는다.
 */
const DataDetailModal: React.FC<Props> = ({ title, subtitle, columns, loading, error, onClose, preview }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'columns' | 'rows'>('columns');
  const [rows, setRows] = useState<{ columns: string[]; rows: any[]; loading: boolean; error?: string } | null>(null);
  useEffect(() => {
    if (tab !== 'rows' || !preview || rows) return;
    setRows({ columns: [], rows: [], loading: true });
    be1Fetch('/api/data/preview', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...preview, limit: 50 }) })
      .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(errorText(j, `HTTP ${r.status}`)); setRows({ columns: j.columns || [], rows: j.rows || [], loading: false }); })
      .catch((e) => setRows({ columns: [], rows: [], loading: false, error: String(e.message || e) }));
  }, [tab, preview, rows]);
  return (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-30" onMouseDown={onClose}>
    <div className="bg-white rounded shadow-lg w-[960px] max-w-[95vw] h-[72vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-800">{title}</div>
          {subtitle && <div className="text-xs text-gray-500 truncate">{subtitle}</div>}
        </div>
        <div className="flex items-center gap-1">
          {preview && (
            <div className="flex rounded border border-gray-300 overflow-hidden text-xs mr-2">
              <button className={`px-2 py-1 ${tab === 'columns' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`} onClick={() => setTab('columns')}>{t('property:dd.columnsTab')}</button>
              <button className={`px-2 py-1 ${tab === 'rows' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`} onClick={() => setTab('rows')}>{t('property:dd.valuesTab')}</button>
            </div>
          )}
          <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('property:dd.close')}>✕</button>
        </div>
      </div>
      {tab === 'rows' ? (
        <div className="p-3 overflow-auto min-h-0 flex-1">
          {!rows || rows.loading ? <div className="text-sm text-gray-500">{t('property:dd.reading')}</div> : rows.error ? <div className="text-sm text-red-500">{rows.error}</div> : rows.rows.length === 0 ? <div className="text-sm text-gray-500">{t('property:dd.noData')}</div> : (
            <table className="text-xs whitespace-nowrap">
              <thead><tr className="text-left text-gray-500 border-b border-gray-200"><th className="py-1 pr-2">#</th>{rows.columns.map((c) => <th key={c} className="py-1 pr-3">{c}</th>)}</tr></thead>
              <tbody>{rows.rows.map((r, i) => <tr key={i} className="border-b border-gray-100"><td className="py-0.5 pr-2 text-gray-400">{i + 1}</td>{rows.columns.map((c) => <td key={c} className="py-0.5 pr-3 text-gray-800 font-mono">{r[c] === null || r[c] === undefined ? <span className="text-gray-300">null</span> : String(r[c])}</td>)}</tr>)}</tbody>
            </table>
          )}
        </div>
      ) : (
      <div className="p-4 overflow-auto min-h-0 flex-1">
        {loading && <div className="text-sm text-gray-500">{t('property:dd.loadingColumns')}</div>}
        {error && <div className="text-sm text-red-500">{error}</div>}
        {!loading && !error && columns.length === 0 && <div className="text-sm text-gray-500">{t('property:dd.noColumns')}</div>}
        {!loading && columns.length > 0 && (
          <table className="min-w-full text-xs">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-1.5 pr-3 w-8">#</th>
                <th className="py-1.5 pr-3">{t('property:dd.colName')}</th>
                <th className="py-1.5 pr-3">{t('property:dd.colType')}</th>
                <th className="py-1.5 pr-3">{t('property:dd.colComment')}</th>
              </tr>
            </thead>
            <tbody>
              {columns.map((c, i) => (
                <tr key={`${c.name}-${i}`} className="border-b border-gray-100">
                  <td className="py-1 pr-3 text-gray-400">{i + 1}</td>
                  <td className="py-1 pr-3 text-gray-800 font-medium">{c.name}</td>
                  <td className="py-1 pr-3 text-gray-600 font-mono">{c.dataType || '-'}</td>
                  <td className="py-1 pr-3 text-gray-500 truncate max-w-[200px]">{c.comment || c.sample || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      )}
      <div className="px-4 py-2 border-t border-gray-200 text-xs text-gray-500 flex justify-between">
        <span>{tab === 'rows' ? t('property:dd.rowsFooter', { n: rows?.rows.length ?? 0 }) : t('property:dd.columnsFooter', { n: columns.length })}</span>
        <button className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700" onClick={onClose}>{t('property:dd.close')}</button>
      </div>
    </div>
  </div>
  );
};

export default DataDetailModal;
